// Truck counts in Inventory › Counts (truck-stock parity 1+2, Brook's van counts and spot checks): ask a
// truck's technician for a count (all of it, or some items), see what they sent in against the books as
// they stood when it was counted, post it with a reason for each difference or discard it; and the
// refill a posted truck count leaves, each line from the warehouse or a vendor, committed or dropped.

import { useState, type ReactNode } from 'react';
import { useDesign } from '../blueprint/design.ts';
import { Empty, Loading, Panel, RowActions, useAct, useQuery } from '../books/ui.tsx';
import { newId } from '../shared/ids.ts';
import { qtyText, type Level, type StockLocation } from './model.ts';
import { COUNT_REASONS, diffText } from './orders-model.ts';
import {
  countedElsewhere,
  countWhen,
  reasonsMissing,
  refillProblems,
  warehouseShort,
  type OpenCount,
  type Refill,
} from './truck-counts-model.ts';
import './inventory.css';

export function TruckCounts({
  locations,
  timeZone,
  toast,
}: {
  locations: StockLocation[];
  timeZone?: string | undefined;
  toast: (m: string) => void;
}) {
  const open = useQuery<{ counts: OpenCount[] }>('stockCounts.open', {});
  const refills = useQuery<{ orders: Refill[] }>('stockCountOrders.list', {});
  const vendors = useQuery<{ vendors: { id: string; name: string }[] }>('vendors.list', {});
  const [asking, setAsking] = useState(false);
  const reload = async () => {
    await Promise.all([open.reload(), refills.reload()]);
  };
  const trucks = locations.filter((l) => l.kind === 'truck' && l.staffId);
  return (
    <Panel
      title="Truck counts"
      aside={
        trucks.length > 0 && (
          <button type="button" className="btn quiet" onClick={() => setAsking(true)}>
            Ask for a count…
          </button>
        )
      }
    >
      {asking && (
        <AskForCount
          trucks={trucks}
          toast={toast}
          onClose={() => setAsking(false)}
          onAsked={() => {
            setAsking(false);
            void reload();
          }}
        />
      )}
      {!open.data ? (
        <Loading error={open.error} what="the counts asked for" retry={() => void open.reload()} />
      ) : open.data.counts.length ? (
        <ul className="rows truck-counts">
          {open.data.counts.map((c) => (
            <li key={c.id}>
              <WaitingCount count={c} timeZone={timeZone} toast={toast} onDone={reload} />
            </li>
          ))}
        </ul>
      ) : (
        <Empty>
          No count is asked for or waiting to post. A technician’s count sent in from their truck shows here.
        </Empty>
      )}
      {refills.data && refills.data.orders.length > 0 && (
        <>
          <h3>Refills the counts left</h3>
          <ul className="rows truck-refills">
            {refills.data.orders.map((r) => (
              <li key={r.id}>
                <RefillCard
                  refill={r}
                  truck={locations.find((l) => l.id === r.locationId)?.name ?? 'The truck'}
                  vendors={vendors.data?.vendors ?? []}
                  toast={toast}
                  onDone={reload}
                />
              </li>
            ))}
          </ul>
        </>
      )}
    </Panel>
  );
}

function AskForCount({
  trucks,
  toast,
  onClose,
  onAsked,
}: {
  trucks: StockLocation[];
  toast: (m: string) => void;
  onClose: () => void;
  onAsked: () => void;
}) {
  const [locationId, setLocationId] = useState(trucks[0]!.id);
  const [some, setSome] = useState(true);
  const [picked, setPicked] = useState<string[] | null>(null);
  const levels = useQuery<{ levels: Level[] }>('stock.levels', { locationId }, [locationId]);
  // the ones flagged for a count start ticked
  const items = levels.data?.levels ?? [];
  const ticked = picked ?? items.filter((l) => l.countNeeded).map((l) => l.itemId);
  const act = useAct(toast, () => undefined);
  const name = trucks.find((t) => t.id === locationId)?.name ?? '';
  const ready = !some || ticked.length > 0;
  return (
    <form
      className="fgrid inline-form ask-count"
      aria-label="Ask for a count"
      onSubmit={(e) => {
        e.preventDefault();
        if (!ready || act.busy) return;
        void act
          .run(
            'stockCounts.request',
            { countId: newId(), locationId, ...(some ? { itemIds: ticked } : {}) },
            `Asked: ${name}’s technician counts it on their iPad.`,
          )
          .then((r) => r && onAsked());
      }}
    >
      <label>
        <span className="flabel">Truck</span>
        <select
          className="field"
          value={locationId}
          onChange={(e) => {
            setLocationId(e.target.value);
            setPicked(null);
          }}
        >
          {trucks.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
      </label>
      <div className="full" role="radiogroup" aria-label="What to count">
        <label className="checkrow">
          <input type="radio" checked={some} onChange={() => setSome(true)} /> Some items (a spot check)
        </label>
        <label className="checkrow">
          <input type="radio" checked={!some} onChange={() => setSome(false)} /> The whole truck
        </label>
      </div>
      {some &&
        (levels.data ? (
          items.length ? (
            <ul className="rows full ask-items" aria-label={`What is stocked on ${name}`}>
              {items.map((l) => (
                <li key={l.itemId}>
                  <label className="checkrow">
                    <input
                      type="checkbox"
                      checked={ticked.includes(l.itemId)}
                      onChange={(e) =>
                        setPicked(
                          e.target.checked ? [...ticked, l.itemId] : ticked.filter((x) => x !== l.itemId),
                        )
                      }
                    />{' '}
                    {l.itemName}
                    {l.countNeeded && <span className="chip warn">Flagged for a count</span>}
                  </label>
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted small full">Nothing is stocked on {name} yet: ask for the whole truck.</p>
          )
        ) : (
          <Loading
            error={levels.error}
            what={`what is stocked on ${name}`}
            retry={() => void levels.reload()}
          />
        ))}
      <div className="row-actions">
        <button type="submit" className="btn primary" disabled={!ready || act.busy}>
          {some
            ? `Ask for ${ticked.length} ${ticked.length === 1 ? 'item' : 'items'}`
            : 'Ask for the whole truck'}
        </button>
        <button type="button" className="btn quiet" onClick={onClose}>
          Cancel
        </button>
      </div>
    </form>
  );
}

/** A truck's count or refill: its own card in Riverstone; in Blueprint a part of Truck counts, so not a
 * section inside the section (DESIGN.md §3a). */
function Card({ className, label, children }: { className: string; label: string; children: ReactNode }) {
  if (useDesign() === 'blueprint')
    return (
      <div className={className} role="group" aria-label={label}>
        {children}
      </div>
    );
  return (
    <article className={`sheet ${className}`} aria-label={label}>
      {children}
    </article>
  );
}

function WaitingCount({
  count: c,
  timeZone,
  toast,
  onDone,
}: {
  count: OpenCount;
  timeZone?: string | undefined;
  toast: (m: string) => void;
  onDone: () => Promise<void>;
}) {
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [tried, setTried] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const [why, setWhy] = useState('');
  const act = useAct(toast, onDone);
  const missing = c.status === 'submitted' ? reasonsMissing(c, reasons) : [];
  const counted = c.lines.filter((l) => l.counted !== null);
  return (
    <Card className="truck-count" label={`${c.locationName}’s count`}>
      <div className="row">
        <b>{c.locationName}</b>
        <span className={`chip${c.status === 'submitted' ? ' warn' : ''}`}>
          {c.status === 'submitted' ? 'Sent in: to post' : 'Asked for'}
        </span>
      </div>
      <p className="muted small">{countWhen(c, timeZone)}</p>
      {c.status === 'requested' && c.scope === 'spot' && (
        <p className="small">{c.lines.map((l) => l.name).join(', ')}</p>
      )}
      {c.status === 'submitted' && (
        <ul className="count-lines" aria-label={`What ${c.locationName}’s technician counted`}>
          {counted.map((l) => {
            const off = Number(l.difference) !== 0;
            // a later count of it is posted: this line leaves it as that count did (QA-863)
            const settled = l.countedAgain?.posted === true;
            const elsewhere = countedElsewhere(l, timeZone);
            return (
              <li key={l.itemId} className={tried && missing.includes(l.itemId) ? 'need' : undefined}>
                <div className="count-name">
                  <b>{l.name}</b>
                </div>
                <div className="count-figs">
                  <span>
                    Counted <b>{qtyText(l.counted!)}</b>
                  </span>
                  {!settled && <span className="muted small">books then {qtyText(l.expected ?? '0')}</span>}
                  {!settled && (
                    <span className={off ? 'off' : 'muted small'}>
                      {off ? diffText(l.difference!) : 'matches'}
                    </span>
                  )}
                </div>
                {elsewhere && <p className="muted small">{elsewhere}</p>}
                {off && !settled && (
                  <label>
                    <span className="flabel">Why it’s off</span>
                    <select
                      className="field"
                      aria-label={`Why ${l.name} is off`}
                      value={reasons[l.itemId] ?? ''}
                      onChange={(e) => setReasons({ ...reasons, [l.itemId]: e.target.value })}
                    >
                      <option value="">Pick a reason…</option>
                      {COUNT_REASONS.map((r) => (
                        <option key={r.value} value={r.value}>
                          {r.label}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {tried && missing.length > 0 && (
        <p className="error small" role="alert">
          Give a reason for every difference.
        </p>
      )}
      {discarding ? (
        <div className="row-actions">
          <label>
            <span className="flabel">Why discard it</span>
            <input className="field" maxLength={300} value={why} onChange={(e) => setWhy(e.target.value)} />
          </label>
          <button
            type="button"
            className="btn"
            disabled={!why.trim() || act.busy}
            onClick={() =>
              void act.run('stockCounts.discard', { countId: c.id, reason: why.trim() }, 'Count discarded.')
            }
          >
            Discard the count
          </button>
          <button type="button" className="btn quiet" onClick={() => setDiscarding(false)}>
            Keep it
          </button>
        </div>
      ) : (
        <div className="row-actions">
          {c.status === 'submitted' && (
            <button
              type="button"
              className="btn primary"
              disabled={act.busy}
              onClick={() => {
                setTried(true);
                if (missing.length) return;
                void act.run<{ lines: { difference: string }[]; orderId: string | null }>(
                  'stockCounts.accept',
                  {
                    countId: c.id,
                    reasons: Object.entries(reasons)
                      .filter(([itemId, r]) => r && counted.some((l) => l.itemId === itemId))
                      .map(([itemId, reason]) => ({ itemId, reason })),
                  },
                  'Count posted. The count flags on the truck are cleared.',
                );
              }}
            >
              Post the count
            </button>
          )}
          <button type="button" className="btn quiet" onClick={() => setDiscarding(true)}>
            Discard…
          </button>
        </div>
      )}
    </Card>
  );
}

function RefillCard({
  refill: r,
  truck,
  vendors,
  toast,
  onDone,
}: {
  refill: Refill;
  truck: string;
  vendors: { id: string; name: string }[];
  toast: (m: string) => void;
  onDone: () => Promise<void>;
}) {
  const act = useAct(toast, onDone);
  const problems = refillProblems(r);
  const setLine = (itemId: string, patch: Record<string, unknown>) =>
    void act.run('stockCountOrders.setLine', { orderId: r.id, itemId, ...patch });
  return (
    <Card className="truck-refill" label={`${truck}’s refill`}>
      <div className="row">
        <b>{truck}’s refill</b>
        <span className="muted small">what its count left under the minimum, up to the maximum</span>
      </div>
      <ul className="count-lines" aria-label={`${truck}’s refill lines`}>
        {r.lines.map((l) => (
          <li key={l.itemId}>
            <div className="count-name">
              <b>{l.name}</b>
              <span className="muted small">the warehouse has {qtyText(l.warehouseOnHand)}</span>
              {warehouseShort(l) && <span className="chip warn">The warehouse is short</span>}
            </div>
            <label>
              <span className="flabel">How many</span>
              <input
                className="field num"
                inputMode="decimal"
                aria-label={`How many ${l.name}`}
                defaultValue={qtyText(l.quantity)}
                onBlur={(e) => {
                  const v = e.target.value.trim();
                  if (v && Number(v) > 0 && Number(v) !== Number(l.quantity))
                    setLine(l.itemId, { quantity: v });
                }}
              />
            </label>
            <label>
              <span className="flabel">From</span>
              <select
                className="field"
                aria-label={`Where ${l.name} comes from`}
                value={l.source === 'warehouse' ? 'warehouse' : (l.vendorId ?? '')}
                onChange={(e) =>
                  setLine(
                    l.itemId,
                    e.target.value === 'warehouse'
                      ? { source: 'warehouse' }
                      : { source: 'vendor', vendorId: e.target.value },
                  )
                }
              >
                <option value="warehouse">The warehouse</option>
                {l.source === 'vendor' && !l.vendorId && <option value="">A vendor: pick one…</option>}
                {vendors.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.name}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="btn quiet"
              aria-label={`Take ${l.name} off`}
              onClick={() => setLine(l.itemId, { remove: true })}
            >
              Take off
            </button>
          </li>
        ))}
      </ul>
      {problems.length > 0 && <p className="muted small">{problems.join(' ')}</p>}
      <RowActions>
        <button
          type="button"
          className="btn primary"
          disabled={act.busy || problems.length > 0}
          onClick={() =>
            void act.run(
              'stockCountOrders.commit',
              { orderId: r.id },
              'Refill committed: the warehouse’s lines are on the truck, the vendors’ on draft POs.',
            )
          }
        >
          Commit the refill
        </button>
        <button
          type="button"
          className="btn quiet"
          disabled={act.busy}
          onClick={() => void act.run('stockCountOrders.discard', { orderId: r.id }, 'Refill dropped.')}
        >
          Drop it
        </button>
      </RowActions>
    </Card>
  );
}
